import type { TokenSet, TokenStore } from '../api/client';
import type { SupportSession } from './authStore';

/**
 * What lets a signed-in tab survive a reload.
 *
 * `sessionStorage`, not `localStorage`: it belongs to one tab and is cleared when that tab closes,
 * so the session ends with the work rather than waiting on a shared reception computer for the
 * next person at the desk. It is still readable by script on the page, which is why what it holds
 * is kept short-lived: the access token lasts minutes, and the refresh token is rotated on every
 * use, with a replayed one revoking the whole session server-side.
 *
 * Every access is guarded. Storage can be disabled, full or blocked by a privacy setting, and a
 * dashboard that cannot remember the session should ask for a password on reload, not fail to start.
 */

const TOKENS_KEY = 'laseera.session.tokens';
const SUPPORT_KEY = 'laseera.session.support';

export const sessionTokenStore: TokenStore = {
  load: () => read(TOKENS_KEY, isTokenSet),
  save: (tokens) => write(TOKENS_KEY, tokens),
};

export function loadSupportSession(): SupportSession | null {
  return read(SUPPORT_KEY, isSupportSession);
}

export function saveSupportSession(session: SupportSession | null): void {
  write(SUPPORT_KEY, session);
}

function read<T>(key: string, isValid: (value: unknown) => value is T): T | null {
  try {
    const raw = globalThis.sessionStorage?.getItem(key);
    if (!raw) {
      return null;
    }
    const value: unknown = JSON.parse(raw);
    return isValid(value) ? value : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown): void {
  try {
    if (value === null) {
      globalThis.sessionStorage?.removeItem(key);
    } else {
      globalThis.sessionStorage?.setItem(key, JSON.stringify(value));
    }
  } catch {
    // Nothing to do: the session still works, it just will not survive a reload.
  }
}

function isTokenSet(value: unknown): value is TokenSet {
  const candidate = value as Partial<TokenSet> | null;
  return typeof candidate?.accessToken === 'string' && typeof candidate.refreshToken === 'string';
}

function isSupportSession(value: unknown): value is SupportSession {
  const candidate = value as Partial<SupportSession> | null;
  return typeof candidate?.clinicId === 'string' && typeof candidate.grantId === 'string';
}
